/**
 * GeoBadge - Компактный индикатор геопозиции
 * 
 * Показывает определённую геопозицию пользователя без блокировки UI
 * 
 * States:
 * - Loading: Skeleton + spinning globe
 * - Unknown: "Location unknown" (fail-open)
 * - Success: "🛡️ 📍 US, New York" или "⚠️ 📍 CU" (if restricted)
 * - VPN: Shows VPN badge if detected
 * 
 * CANON v1.3.0 Compliant:
 * - Semantic tokens only (no raw hex/px)
 * - 240ms canonical motion
 * - Skeleton-first loading
 * - WCAG 2.2 AA a11y
 * 
 * Owner: @i (Auth Flow Optimization)
 * Created: 2025-11-05
 */

import { Skeleton } from '@/components/ui/skeleton';
import { isCountrySupported } from '@/lib/supportedCountries';
import { cn } from '@/lib/utils';
import { motion } from '@/ui/tokens';
import { semanticTokens } from '@packages/config/theme';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { Globe } from 'lucide-react';
import React from 'react';
import { CircleFlag } from 'react-circle-flags';

interface GeoBadgeProps {
  isDetecting: boolean;
  country: string | null;
  city: string | null;
  vpnDetected: boolean;
  hasRestrictions: boolean;
  confidence: 'high' | 'medium' | 'low';
  className?: string;
}

export const GeoBadge: React.FC<GeoBadgeProps> = ({
  isDetecting,
  country,
  city,
  vpnDetected,
  hasRestrictions,
  confidence,
  className
}) => {
  // Loading state - minimal skeleton
  if (isDetecting) {
    return (
      <div className={cn("flex items-center gap-1.5", className)}>
        <Globe
          className={cn("w-3.5 h-3.5 text-muted-foreground", motion.animations.pulse)}
          aria-label="Detecting location"
        />
        <Skeleton className="h-3.5 w-24" />
      </div>
    );
  }

  // No location detected - subtle text only
  if (!country) {
    return null; // Fail silently - no need to show "unknown"
  }

  // Normalize country code for CircleFlag (must be lowercase 2-letter code)
  const normalizedCountryCode = country.toLowerCase().trim();
  
  // Determine country support status
  const isSupported = isCountrySupported(country);

  // Determine status indicator based on restrictions and support
  let statusConfig: {
    icon: string;
    color: string;
    label: string;
    textColor: string;
  };

  if (hasRestrictions) {
    statusConfig = {
      icon: sfIcons.alertTriangle,
      color: semanticTokens.warning.fg,
      label: 'Limited',
      textColor: semanticTokens.warning.fg
    };
  } else if (isSupported) {
    statusConfig = {
      icon: sfIcons.success,
      color: semanticTokens.positive.fg,
      label: 'Available',
      textColor: semanticTokens.fg.muted
    };
  } else {
    statusConfig = {
      icon: sfIcons.error,
      color: semanticTokens.danger.fg,
      label: 'Not Available',
      textColor: semanticTokens.danger.fg
    };
  }

  return (
    <div
      className={cn(
        "flex items-center gap-1.5 text-xs animate-in fade-in-50 duration-240",
        statusConfig.textColor,
        className
      )}
      role="status"
      aria-live="polite"
      aria-label={`Service ${statusConfig.label.toLowerCase()} in ${country}`}
    >
      <div className="w-6 h-6 shrink-0 overflow-hidden rounded-full flex items-center justify-center" aria-hidden="true">
        <CircleFlag
          countryCode={normalizedCountryCode}
          height={24}
          width={24}
        />
      </div>
      <span className="font-medium">
        {country}
      </span>
      <SFIcon
        icon={statusConfig.icon}
        className={cn("w-3.5 h-3.5", statusConfig.color)}
        aria-hidden="true"
      />
      <span className={cn("text-xs font-medium", statusConfig.color)}>
        {statusConfig.label}
      </span>
      {vpnDetected && (
        <span className={cn("text-xs", semanticTokens.accent.fg)}>(VPN)</span>
      )}
    </div>
  );
};

